import React, { useMemo, useState } from 'react';
import { historyTotals, rankVersesByErrors } from '../services/historyStorage';
import '../styles/RecitationHistory.css';

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
});

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

const percent = (value) => `${Math.round(value * 100)}%`;

/**
 * Historique local : versets les plus fautifs et examens passés.
 * Tout provient de localStorage, rien n'est envoyé au serveur.
 */
const RecitationHistory = ({ history, onClear }) => {
  const [open, setOpen] = useState(false);

  const totals = useMemo(() => historyTotals(history), [history]);
  const ranked = useMemo(() => rankVersesByErrors(history, 10), [history]);
  const exams = history?.exams || [];

  if (!totals.attempts && exams.length === 0) {
    return null;
  }

  return (
    <section className="recitation-history">
      <button
        type="button"
        className="history-toggle"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span>Mon historique</span>
        <span className="history-summary">
          {totals.distinctVerses} verset{totals.distinctVerses > 1 ? 's' : ''} ·{' '}
          {totals.errors} erreur{totals.errors > 1 ? 's' : ''} ·{' '}
          {percent(totals.accuracy)} de précision
        </span>
        <span className="history-chevron">{open ? '▼' : '▶'}</span>
      </button>

      {open && (
        <div className="history-body">
          <div className="history-block">
            <h4>Versets à retravailler</h4>
            {ranked.length === 0 ? (
              <p className="history-empty">Aucune erreur enregistrée pour l’instant.</p>
            ) : (
              <table className="history-table" aria-label="Versets classés par erreurs">
                <thead>
                  <tr>
                    <th>Verset</th><th>Tentatives</th><th>Corrects</th>
                    <th>Erreurs</th><th>Dernier essai</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((verse) => (
                    <tr key={`${verse.surah}:${verse.ayah}`}>
                      <td>{verse.surah}:{verse.ayah}</td>
                      <td>{verse.attempts}</td>
                      <td>{verse.correct}</td>
                      <td className="history-errors">{verse.errors}</td>
                      <td>{formatDate(verse.lastAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="history-block">
            <h4>Examens passés</h4>
            {exams.length === 0 ? (
              <p className="history-empty">Aucun examen enregistré.</p>
            ) : (
              <ul className="history-exams" aria-label="Historique des examens">
                {exams.map((exam) => (
                  <li key={exam.id}>
                    <span className="exam-date">{formatDate(exam.startedAt)}</span>
                    <span className="exam-scope">{exam.scope || 'Examen'}</span>
                    <span className="exam-score">
                      {exam.passed} réussie{exam.passed > 1 ? 's' : ''} ·{' '}
                      {exam.toReview} à revoir
                      {exam.questionCount ? ` / ${exam.questionCount}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button type="button" className="btn btn-outline history-clear" onClick={onClear}>
            Effacer l’historique
          </button>
        </div>
      )}
    </section>
  );
};

export default RecitationHistory;
